import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft, LogOut, Loader2, Users, Building2, Video, FileSignature, Star,
  MessageSquare, BadgeCheck, X, Check, Megaphone, CalendarClock, Eye, TrendingUp, Bell, DollarSign, HeartHandshake,
  Mic, AlertTriangle, CheckCircle2, ExternalLink
} from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import ExtrasAdmin from "@/components/ExtrasAdmin";
import CommunityPartnersAdmin from "@/components/CommunityPartnersAdmin";
import AdminClaimsQueue from "@/components/AdminClaimsQueue";
import LegalConsentAdmin from "@/components/LegalConsentAdmin";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIER_BADGE = {
  listed:     "bg-[#F1F5F9] text-[#475569]",
  essential:  "bg-[#EDF7F6] text-primary",
  enhanced:   "bg-[#E0F2F1] text-[#0F766E]",
  premium:    "bg-[#EDE9FE] text-[#5B21B6]",
  enterprise: "bg-[#FEF3C7] text-[#92400E]",
};

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const [overview, setOverview] = useState(null);
  const [pending, setPending] = useState([]);
  const [providerAccounts, setProviderAccounts] = useState([]);
  const [promo, setPromo] = useState(null);
  const [savingPromo, setSavingPromo] = useState(false);
  const [partnerInquiries, setPartnerInquiries] = useState([]);
  const [providerInquiries, setProviderInquiries] = useState([]);
  const [ttsHealth, setTtsHealth] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const [ov, pend, accounts, pr, cpi, pi] = await Promise.all([
        axios.get(`${API}/admin/overview`),
        axios.get(`${API}/admin/pending-providers`),
        axios.get(`${API}/admin/provider-accounts`),
        axios.get(`${API}/promo`),
        axios.get(`${API}/admin/community-partner/inquiries`),
        axios.get(`${API}/admin/provider/inquiries`),
      ]);
      setOverview(ov.data);
      setPending(pend.data);
      setProviderAccounts(accounts.data);
      setPromo(pr.data);
      setPartnerInquiries(cpi.data);
      setProviderInquiries(pi.data);
      // TTS health lives on its own request so a slow ElevenLabs probe never
      // blocks the rest of the admin dashboard from rendering.
      axios.get(`${API}/admin/tts-health`)
        .then((r) => setTtsHealth(r.data))
        .catch(() => setTtsHealth({ probe_status: "probe_error", probe_error: "Health check could not be reached." }));
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Admin access required");
        navigate("/portal");
      }
    }
  }, [navigate]);

  useEffect(() => {
    if (!loading && !user) navigate("/auth");
    if (user) loadAll();
  }, [user, loading, navigate, loadAll]);

  if (loading || !user || !overview) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#EDF7F6] to-[#FAF9F6] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const handleApprove = async (p) => {
    try {
      const { data } = await axios.post(`${API}/admin/providers/${p.id}/approve`);
      toast.success(data.message);
      loadAll();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleReject = async (p) => {
    try {
      await axios.post(`${API}/admin/providers/${p.id}/reject`);
      toast.success(`${p.name} rejected`);
      loadAll();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleApprovePartnerInquiry = async (inq) => {
    if (!window.confirm(`Approve "${inq.organization_name}" as a Community Partner? This creates a live listing immediately.`)) return;
    try {
      const { data } = await axios.post(`${API}/admin/community-partner/inquiries/${inq.id}/approve`);
      toast.success(data.message + (data.zip_codes_parsed?.length ? ` (${data.zip_codes_parsed.length} zip${data.zip_codes_parsed.length===1?"":"s"} parsed)` : " — no zip codes detected; edit the listing to add coverage"));
      loadAll();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleDeclinePartnerInquiry = async (inq) => {
    if (!window.confirm(`Decline the inquiry from "${inq.organization_name}"?`)) return;
    try {
      await axios.post(`${API}/admin/community-partner/inquiries/${inq.id}/decline`);
      toast.success("Inquiry declined");
      loadAll();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleProviderInquiryStatus = async (inq, nextStatus) => {
    // Approving or declining is terminal — confirm those; the other transitions
    // (in_review / contacted) are lightweight bookkeeping so we skip the prompt.
    const isTerminal = nextStatus === "approved" || nextStatus === "declined";
    if (isTerminal) {
      const verb = nextStatus === "approved" ? "approve" : "decline";
      if (!window.confirm(`Are you sure you want to ${verb} the inquiry from "${inq.organization_name}"?`)) return;
    }
    try {
      await axios.post(`${API}/admin/provider/inquiries/${inq.id}/status`, { status: nextStatus });
      const label = { in_review: "moved to In Review", contacted: "marked Contacted", approved: "approved", declined: "declined" }[nextStatus] || "updated";
      toast.success(`${inq.organization_name} ${label}`);
      loadAll();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleSavePromo = async () => {
    setSavingPromo(true);
    try {
      await axios.put(`${API}/admin/promo`, {
        enabled: promo.enabled,
        title: promo.title,
        description: promo.description
      });
      toast.success("Launch promo updated");
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setSavingPromo(false);
    }
  };

  const stats = [
    { label: "Revenue (30d)", value: `$${Number(overview.revenue?.last_30d || 0).toLocaleString()}`, icon: DollarSign },
    { label: "Total Revenue", value: `$${Number(overview.revenue?.total || 0).toLocaleString()}`, icon: DollarSign },
    { label: "Family Accounts", value: overview.accounts.families, icon: Users },
    { label: "Provider Accounts", value: overview.accounts.providers, icon: Building2 },
    { label: "New Accounts (7d)", value: overview.accounts.new_7d, icon: TrendingUp },
    { label: "Listed (Free)", value: overview.listings.listed, icon: BadgeCheck },
    { label: "Essential", value: overview.listings.essential, icon: BadgeCheck },
    { label: "Enhanced", value: overview.listings.enhanced, icon: BadgeCheck },
    { label: "Premium", value: overview.listings.premium, icon: BadgeCheck },
    { label: "Enterprise", value: overview.listings.enterprise, icon: BadgeCheck },
    { label: "Consults (30d)", value: overview.activity.consults_30d, icon: Video },
    { label: "Forms Signed", value: overview.activity.forms_signed, icon: FileSignature },
    { label: "Reviews", value: overview.activity.reviews, icon: Star },
    { label: "Asher Conversations", value: overview.activity.conversations, icon: MessageSquare },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#EDF7F6] to-[#FAF9F6]">
      <header className="bg-primary shadow-md">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button data-testid="admin-back-btn" onClick={() => navigate("/")} variant="ghost" className="text-white hover:bg-white/10 px-2">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-heading font-bold text-white">Tyvena Company Dashboard</h1>
          </div>
          <Button
            data-testid="admin-logout-btn"
            onClick={async () => { await logout(); navigate("/"); }}
            variant="outline" className="border-2 border-white/40 text-white bg-transparent hover:bg-white/10 rounded-full font-body font-semibold"
          >
            <LogOut className="w-4 h-4 mr-2" /> Sign Out
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Stats grid */}
        <section data-testid="admin-stats-section" className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {stats.map((s) => (
            <div key={s.label} className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0] p-4">
              <s.icon className="w-4 h-4 text-primary mb-2" />
              <p className="text-2xl font-heading font-bold text-text-primary">{s.value}</p>
              <p className="text-[11px] font-body font-semibold text-text-secondary uppercase tracking-wide mt-0.5">{s.label}</p>
            </div>
          ))}
        </section>

        {/* System Health — surfaces ElevenLabs TTS status live so admins spot
            quota issues before families do. Card color reflects severity. */}
        {ttsHealth && (() => {
          const s = ttsHealth.probe_status;
          const isOk = s === "healthy" || s === "no_data";
          const isWarn = s === "low_credits";
          const isBad = s === "quota_exhausted" || s === "failing" || s === "not_configured" || (s || "").startsWith("probe_");
          const bg = isOk ? "bg-emerald-50 border-emerald-200" : isWarn ? "bg-amber-50 border-amber-200" : "bg-red-50 border-red-200";
          const iconColor = isOk ? "text-emerald-600" : isWarn ? "text-amber-600" : "text-red-600";
          const Icon = isOk ? CheckCircle2 : AlertTriangle;
          const remaining = ttsHealth.remaining_credits;
          const limit = ttsHealth.limit_credits;
          const runtime = ttsHealth.runtime || {};
          const consecFails = runtime.consecutive_failures || 0;

          const headline = ({
            healthy: "Asher's voice is healthy",
            low_credits: "Voice credits running low — top up soon",
            quota_exhausted: "Asher's voice is DOWN — credits exhausted",
            failing: "Asher's voice is failing — investigate ElevenLabs",
            not_configured: "Asher's voice is not configured on the server",
            no_data: "Asher's voice — no calls yet this session",
            probe_error: "Voice health probe couldn't reach ElevenLabs",
          })[s] || "Asher's voice health";

          return (
            <section data-testid="admin-tts-health-section" className={`rounded-2xl border-2 p-6 ${bg}`}>
              <div className="flex items-start gap-4">
                <div className={`p-2 rounded-full bg-white ${iconColor}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Mic className={`w-4 h-4 ${iconColor}`} />
                    <h2 className="text-lg font-heading font-semibold text-text-primary" data-testid="tts-health-headline">
                      {headline}
                    </h2>
                  </div>
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm font-body">
                    <div>
                      <p className="text-text-secondary text-xs uppercase tracking-wide">Credits remaining</p>
                      <p className="text-text-primary font-semibold mt-0.5" data-testid="tts-credits-remaining">
                        {remaining !== null && remaining !== undefined
                          ? `${Number(remaining).toLocaleString()}${limit ? ` / ${Number(limit).toLocaleString()}` : ""}`
                          : "—"}
                      </p>
                    </div>
                    <div>
                      <p className="text-text-secondary text-xs uppercase tracking-wide">Success / Failure (session)</p>
                      <p className="text-text-primary font-semibold mt-0.5">
                        {runtime.total_success || 0} / {runtime.total_failure || 0}
                        {consecFails > 0 && (
                          <span className="ml-2 text-red-700">
                            ({consecFails} in a row)
                          </span>
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-text-secondary text-xs uppercase tracking-wide">Last failure</p>
                      <p className="text-text-primary font-semibold mt-0.5">
                        {runtime.last_failure_reason
                          ? runtime.last_failure_reason
                          : "None"}
                      </p>
                    </div>
                  </div>
                  {(isWarn || isBad) && (
                    <div className="mt-4 flex flex-wrap items-center gap-3">
                      <a
                        href={ttsHealth.top_up_url || "https://elevenlabs.io/app/subscription"}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 rounded-full bg-primary text-white px-4 py-2 text-sm font-body font-semibold hover:bg-primary/90"
                        data-testid="tts-top-up-link"
                      >
                        Top up ElevenLabs credits <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                      {ttsHealth.probe_error && (
                        <span className="text-xs text-text-secondary italic" title={ttsHealth.probe_error}>
                          Probe: {ttsHealth.probe_error.slice(0, 80)}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </section>
          );
        })()}

        {/* Pending approvals */}
        <section data-testid="admin-pending-section" className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0] p-6 md:p-8">
          <div className="flex items-center gap-3 mb-4">
            <BadgeCheck className="w-6 h-6 text-primary" />
            <h2 className="text-2xl font-heading font-semibold text-text-primary">Pending Provider Approvals</h2>
            {pending.length > 0 && (
              <span className="px-3 py-1 rounded-full text-xs font-bold bg-[#FDE68A] text-[#92400E]">{pending.length}</span>
            )}
          </div>
          {pending.length === 0 ? (
            <p className="text-text-secondary font-body py-2">No listings waiting for review. 🎉</p>
          ) : (
            <div className="space-y-3">
              {pending.map((p) => (
                <div key={p.id} data-testid={`pending-provider-${p.id}`} className="flex flex-wrap items-center justify-between gap-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-4">
                  <div>
                    <p className="font-body font-bold text-text-primary flex items-center gap-2">
                      {p.name}
                      <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase ${TIER_BADGE[p.subscription?.tier] || TIER_BADGE.listed}`}>
                        {p.subscription?.tier}
                      </span>
                    </p>
                    <p className="text-sm font-body text-text-secondary">
                      {(p.care_types || []).join(", ")} · {p.email} · {p.phone} · zips: {(p.zip_codes || []).join(", ")}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      data-testid={`approve-provider-${p.id}`}
                      onClick={() => handleApprove(p)}
                      className="bg-green-600 hover:bg-green-700 text-white rounded-full font-bold font-body"
                    >
                      <Check className="w-4 h-4 mr-2" /> Approve
                    </Button>
                    <Button
                      data-testid={`reject-provider-${p.id}`}
                      onClick={() => handleReject(p)}
                      variant="outline" className="border-2 border-red-300 text-red-600 hover:bg-red-50 rounded-full font-bold font-body"
                    >
                      <X className="w-4 h-4 mr-2" /> Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Provider Claims Queue — NPI-verified claim requests */}
        <AdminClaimsQueue />

        {/* Community Partner Inquiries */}
        <section data-testid="admin-partner-inquiries-section" className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0] p-6 md:p-8">
          <div className="flex items-center gap-3 mb-4">
            <HeartHandshake className="w-6 h-6 text-secondary" />
            <h2 className="text-2xl font-heading font-semibold text-text-primary">Community Partner Inquiries</h2>
            <span className="text-sm font-body text-text-secondary">
              ({partnerInquiries.filter(i => i.status === "new").length} new, {partnerInquiries.length} total)
            </span>
          </div>

          {partnerInquiries.length === 0 ? (
            <p className="text-text-secondary font-body italic">No inquiries yet. Nonprofit orgs submit these via the "Are you a nonprofit?" callout on the provider inquiry page.</p>
          ) : (
            <div className="space-y-3">
              {partnerInquiries.map((inq) => {
                const statusColor = {
                  new: "bg-amber-100 text-amber-800 border-amber-300",
                  in_review: "bg-blue-100 text-blue-800 border-blue-300",
                  approved: "bg-green-100 text-green-800 border-green-300",
                  declined: "bg-gray-100 text-gray-700 border-gray-300",
                }[inq.status] || "bg-gray-100 text-gray-700 border-gray-300";
                return (
                  <div
                    key={inq.id}
                    data-testid={`inquiry-row-${inq.id}`}
                    className="flex flex-col md:flex-row md:items-start gap-4 p-4 rounded-xl bg-[#FAF9F6] border border-[#E2E8F0]"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center flex-wrap gap-2">
                        <p className="font-body font-bold text-text-primary">{inq.organization_name}</p>
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${statusColor}`}>
                          {inq.status}
                        </span>
                        <span className="text-xs font-body text-text-secondary bg-white border border-[#E2E8F0] rounded-full px-2 py-0.5">
                          {inq.org_type}
                        </span>
                      </div>
                      <p className="text-sm font-body text-text-secondary mt-1">
                        {inq.contact_name} · <a href={`mailto:${inq.email}`} className="text-primary hover:underline">{inq.email}</a> · {inq.phone}
                      </p>
                      {inq.service_area && (
                        <p className="text-xs font-body text-text-secondary mt-1">Service area: {inq.service_area}</p>
                      )}
                      <p className="text-sm font-body text-text-primary mt-2 whitespace-pre-wrap">{inq.message}</p>
                      <p className="text-[11px] font-body text-text-secondary mt-2">
                        Received {new Date(inq.created_at).toLocaleDateString()} at {new Date(inq.created_at).toLocaleTimeString()}
                      </p>
                    </div>
                    {inq.status !== "approved" && inq.status !== "declined" && (
                      <div className="flex md:flex-col gap-2 flex-shrink-0">
                        <Button
                          data-testid={`approve-inquiry-${inq.id}`}
                          onClick={() => handleApprovePartnerInquiry(inq)}
                          className="bg-secondary text-white hover:bg-secondary/90 rounded-full font-bold font-body"
                          size="sm"
                        >
                          <Check className="w-4 h-4 mr-1" /> Approve
                        </Button>
                        <Button
                          data-testid={`decline-inquiry-${inq.id}`}
                          onClick={() => handleDeclinePartnerInquiry(inq)}
                          variant="outline"
                          className="border-2 border-red-300 text-red-600 hover:bg-red-50 rounded-full font-bold font-body"
                          size="sm"
                        >
                          <X className="w-4 h-4 mr-1" /> Decline
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Provider Interest Queue — inquiries from prospective care providers
            submitting the public /provider-signup form. Lifecycle: new → in_review
            → contacted → approved | declined. Approving does NOT auto-create a
            live listing; it just marks the inquiry — the actual onboarding is
            done manually via /provider-onboarding once the admin invites them. */}
        <section data-testid="admin-provider-inquiries-section" className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0] p-6 md:p-8">
          <div className="flex items-center gap-3 mb-4">
            <Building2 className="w-6 h-6 text-primary" />
            <h2 className="text-2xl font-heading font-semibold text-text-primary">Provider Interest</h2>
            <span className="text-sm font-body text-text-secondary">
              ({providerInquiries.filter(i => i.status === "new").length} new, {providerInquiries.length} total)
            </span>
          </div>
          <p className="text-sm font-body text-text-secondary mb-5">
            Submissions from prospective providers via <code>/provider-signup</code>. Move each through the funnel as you work it. Only &ldquo;Approved&rdquo; providers should receive a manual invite to <code>/provider-onboarding</code>.
          </p>

          {providerInquiries.length === 0 ? (
            <p className="text-text-secondary font-body italic">
              No provider inquiries yet. Submissions land here when someone fills out the interest form on <code>/provider-signup</code>.
            </p>
          ) : (
            <div className="space-y-3">
              {providerInquiries.map((inq) => {
                const statusColor = {
                  new:        "bg-amber-100 text-amber-800 border-amber-300",
                  in_review:  "bg-blue-100 text-blue-800 border-blue-300",
                  contacted:  "bg-purple-100 text-purple-800 border-purple-300",
                  approved:   "bg-green-100 text-green-800 border-green-300",
                  declined:   "bg-gray-100 text-gray-700 border-gray-300",
                }[inq.status] || "bg-gray-100 text-gray-700 border-gray-300";
                const isTerminal = inq.status === "approved" || inq.status === "declined";
                return (
                  <div
                    key={inq.id}
                    data-testid={`provider-inquiry-row-${inq.id}`}
                    className="flex flex-col md:flex-row md:items-start gap-4 p-4 rounded-xl bg-[#FAF9F6] border border-[#E2E8F0]"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center flex-wrap gap-2">
                        <p className="font-body font-bold text-text-primary">{inq.organization_name}</p>
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${statusColor}`}>
                          {inq.status.replace("_", " ")}
                        </span>
                      </div>
                      <p className="text-sm font-body text-text-secondary mt-1">
                        {inq.contact_name} · <a href={`mailto:${inq.email}`} className="text-primary hover:underline">{inq.email}</a> · <a href={`tel:${inq.phone}`} className="text-primary hover:underline">{inq.phone}</a>
                      </p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {inq.care_types?.split(",").map((c) => (
                          <span
                            key={c}
                            className="text-[11px] font-body font-medium bg-primary/10 text-primary rounded-full px-2.5 py-0.5"
                          >
                            {c.trim()}
                          </span>
                        ))}
                      </div>
                      {inq.service_area && (
                        <p className="text-xs font-body text-text-secondary mt-2">Service area: {inq.service_area}</p>
                      )}
                      {inq.message && (
                        <p className="text-sm font-body text-text-primary mt-2 whitespace-pre-wrap">{inq.message}</p>
                      )}
                      <p className="text-[11px] font-body text-text-secondary mt-2">
                        Received {new Date(inq.created_at).toLocaleDateString()} at {new Date(inq.created_at).toLocaleTimeString()}
                        {inq.reviewed_by && ` · last touched by ${inq.reviewed_by}`}
                      </p>
                    </div>
                    {!isTerminal && (
                      <div className="flex md:flex-col gap-2 flex-shrink-0 min-w-[140px]">
                        {inq.status === "new" && (
                          <Button
                            data-testid={`pi-review-${inq.id}`}
                            onClick={() => handleProviderInquiryStatus(inq, "in_review")}
                            variant="outline"
                            className="border-2 border-blue-300 text-blue-700 hover:bg-blue-50 rounded-full font-bold font-body"
                            size="sm"
                          >
                            <Eye className="w-4 h-4 mr-1" /> Reviewing
                          </Button>
                        )}
                        {(inq.status === "new" || inq.status === "in_review") && (
                          <Button
                            data-testid={`pi-contacted-${inq.id}`}
                            onClick={() => handleProviderInquiryStatus(inq, "contacted")}
                            variant="outline"
                            className="border-2 border-purple-300 text-purple-700 hover:bg-purple-50 rounded-full font-bold font-body"
                            size="sm"
                          >
                            <MessageSquare className="w-4 h-4 mr-1" /> Contacted
                          </Button>
                        )}
                        <Button
                          data-testid={`pi-approve-${inq.id}`}
                          onClick={() => handleProviderInquiryStatus(inq, "approved")}
                          className="bg-secondary text-white hover:bg-secondary/90 rounded-full font-bold font-body"
                          size="sm"
                        >
                          <Check className="w-4 h-4 mr-1" /> Approve
                        </Button>
                        <Button
                          data-testid={`pi-decline-${inq.id}`}
                          onClick={() => handleProviderInquiryStatus(inq, "declined")}
                          variant="outline"
                          className="border-2 border-red-300 text-red-600 hover:bg-red-50 rounded-full font-bold font-body"
                          size="sm"
                        >
                          <X className="w-4 h-4 mr-1" /> Decline
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <CommunityPartnersAdmin />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Launch promo */}
          <section data-testid="admin-promo-section" className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0] p-6 md:p-8">
            <div className="flex items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-3">
                <Megaphone className="w-6 h-6 text-primary" />
                <h2 className="text-xl font-heading font-semibold text-text-primary">Launch Special</h2>
              </div>
              <Switch
                data-testid="promo-enabled-switch"
                checked={promo?.enabled || false}
                onCheckedChange={(v) => setPromo((p) => ({ ...p, enabled: v }))}
              />
            </div>
            <p className="text-sm font-body text-text-secondary mb-4">
              Shown on the provider signup page and provider dashboards to promote current offers.
            </p>
            <div className="space-y-3">
              <div>
                <Label className="font-body font-semibold text-sm">Headline</Label>
                <Input
                  data-testid="promo-title-input"
                  value={promo?.title || ""}
                  onChange={(e) => setPromo((p) => ({ ...p, title: e.target.value }))}
                  className="mt-1 font-body"
                />
              </div>
              <div>
                <Label className="font-body font-semibold text-sm">Offer Text</Label>
                <Textarea
                  data-testid="promo-description-input"
                  value={promo?.description || ""}
                  onChange={(e) => setPromo((p) => ({ ...p, description: e.target.value }))}
                  rows={3}
                  className="mt-1 font-body"
                />
              </div>
              <Button
                data-testid="promo-save-btn"
                onClick={handleSavePromo}
                disabled={savingPromo}
                className="bg-primary text-white hover:bg-[#238C82] rounded-full font-bold font-body"
              >
                {savingPromo ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Promo"}
              </Button>
            </div>
          </section>

          {/* Renewals */}
          <section data-testid="admin-renewals-section" className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0] p-6 md:p-8">
            <div className="flex items-center gap-3 mb-4">
              <CalendarClock className="w-6 h-6 text-primary" />
              <h2 className="text-xl font-heading font-semibold text-text-primary">Subscription Renewals</h2>
            </div>
            <p className="text-sm font-body text-text-secondary mb-4">
              Subscriptions auto-renew monthly. Providers get an email notice 48 hours before renewal (checked hourly).
            </p>
            <p className="text-xs font-body font-bold text-text-secondary uppercase tracking-wide mb-2">Renewing in the next 7 days</p>
            {overview.upcoming_renewals.length === 0 ? (
              <p className="text-sm text-text-secondary font-body mb-4">None upcoming.</p>
            ) : (
              <div className="space-y-2 mb-4">
                {overview.upcoming_renewals.map((r) => (
                  <div key={r.id} className="flex items-center justify-between gap-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3 text-sm font-body">
                    <span className="font-bold text-text-primary">{r.name}</span>
                    <span className="text-text-secondary">{new Date(r.renewal_date).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs font-body font-bold text-text-secondary uppercase tracking-wide mb-2 flex items-center gap-1">
              <Bell className="w-3 h-3" /> Recent renewal notices
            </p>
            {overview.renewal_notices.length === 0 ? (
              <p className="text-sm text-text-secondary font-body">No notices sent yet.</p>
            ) : (
              <div className="space-y-2">
                {overview.renewal_notices.slice(0, 5).map((n) => (
                  <div key={n.id} className="flex items-center justify-between gap-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3 text-xs font-body">
                    <span className="font-bold text-text-primary truncate">{n.provider_name}</span>
                    <span className={`px-2 py-0.5 rounded-full font-bold ${n.status === "sent" ? "bg-green-100 text-green-700" : "bg-[#FDE68A] text-[#92400E]"}`}>
                      {n.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        {/* Provider accounts - view as */}
        <section data-testid="admin-provider-accounts-section" className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0] p-6 md:p-8">
          <div className="flex items-center gap-3 mb-4">
            <Eye className="w-6 h-6 text-primary" />
            <h2 className="text-2xl font-heading font-semibold text-text-primary">Provider Accounts</h2>
          </div>
          <p className="text-sm font-body text-text-secondary mb-4">
            See exactly what each provider sees — open their dashboard in view-as mode.
          </p>
          {providerAccounts.length === 0 ? (
            <p className="text-text-secondary font-body">No provider accounts yet.</p>
          ) : (
            <div className="space-y-2">
              {providerAccounts.map((a) => (
                <div key={a.email} className="flex flex-wrap items-center justify-between gap-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-4">
                  <div>
                    <p className="font-body font-bold text-text-primary">{a.name}</p>
                    <p className="text-sm font-body text-text-secondary">{a.email}</p>
                  </div>
                  <Button
                    data-testid={`view-as-${a.email}`}
                    onClick={() => navigate(`/provider-dashboard?as=${encodeURIComponent(a.email)}`)}
                    variant="outline" className="border-2 border-primary text-primary hover:bg-primary/10 rounded-full font-body font-semibold"
                  >
                    <Eye className="w-4 h-4 mr-2" /> View Their Dashboard
                  </Button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Recent accounts */}
        <section data-testid="admin-recent-accounts-section" className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0] p-6 md:p-8">
          <div className="flex items-center gap-3 mb-4">
            <Users className="w-6 h-6 text-primary" />
            <h2 className="text-2xl font-heading font-semibold text-text-primary">Recent Accounts</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm font-body">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-text-secondary border-b border-[#E2E8F0]">
                  <th className="py-2 pr-4">Name</th>
                  <th className="py-2 pr-4">Email</th>
                  <th className="py-2 pr-4">Role</th>
                  <th className="py-2 pr-4">Sign-in</th>
                  <th className="py-2">Created</th>
                </tr>
              </thead>
              <tbody>
                {overview.recent_accounts.map((a) => (
                  <tr key={a.email} className="border-b border-[#F1F5F9]">
                    <td className="py-2.5 pr-4 font-bold text-text-primary">{a.name}</td>
                    <td className="py-2.5 pr-4 text-text-secondary">{a.email}</td>
                    <td className="py-2.5 pr-4">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                        a.role === "admin" ? "bg-primary/10 text-primary" : a.role === "provider" ? "bg-[#FDE9D9] text-[#9A5B2D]" : "bg-[#E0F2F1] text-[#00695C]"
                      }`}>{a.role}</span>
                    </td>
                    <td className="py-2.5 pr-4 text-text-secondary capitalize">{a.auth_provider}</td>
                    <td className="py-2.5 text-text-secondary">{new Date(a.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <ExtrasAdmin />
        <LegalConsentAdmin />
      </main>
    </div>
  );
}
